import React, { useState, useEffect } from 'react';
import { Project, Client } from '../types';
import { getProjects, getClients, createProject, updateProject, deleteProject } from '../api/client';

function ProjectForm({
  initial,
  clients,
  onSave,
  onCancel,
}: {
  initial?: Partial<Project>;
  clients: Client[];
  onSave: (data: { name: string; clientId: string; status: string; notes: string }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(initial?.name || '');
  const [clientId, setClientId] = useState(initial?.clientId || '');
  const [status, setStatus] = useState(initial?.status || 'active');
  const [notes, setNotes] = useState(initial?.notes || '');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientId) return;
    setSaving(true);
    try {
      await onSave({ name, clientId, status, notes });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>Project Name</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g., Website Redesign"
          required
          autoFocus
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label>Client *</label>
          <select value={clientId} onChange={(e) => setClientId(e.target.value)} required>
            <option value="">Select client...</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Status</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="completed">Completed</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>
      <div>
        <label>Goals &amp; Background</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Project goals, context, key details, background info..."
          rows={4}
          style={{ resize: 'vertical' }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
        <button type="button" onClick={onCancel} className="btn btn-secondary btn-sm">Cancel</button>
        <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !clientId}>
          {saving ? 'Saving...' : initial?.id ? 'Update Project' : 'Create Project'}
        </button>
      </div>
    </form>
  );
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  active: { label: 'Active', color: '#16A34A', bg: '#F0FDF4' },
  paused: { label: 'Paused', color: '#D97706', bg: '#FFFBEB' },
  completed: { label: 'Completed', color: '#2563EB', bg: '#EFF6FF' },
  archived: { label: 'Archived', color: '#64748B', bg: '#F1F5F9' },
};

export default function ProjectsPage() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [filterClient, setFilterClient] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    Promise.all([getProjects(), getClients()])
      .then(([p, c]) => { setProjects(p); setClients(c); })
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async (data: { name: string; clientId: string; status: string; notes: string }) => {
    const project = await createProject(data);
    setProjects((prev) => [...prev, project]);
    setShowAdd(false);
  };

  const handleUpdate = async (id: string, data: { name: string; clientId: string; status: string; notes: string }) => {
    const project = await updateProject(id, data);
    setProjects((prev) => prev.map((p) => (p.id === id ? project : p)));
    setEditingId(null);
  };

  const handleDelete = async (id: string) => {
    if (deletingId !== id) { setDeletingId(id); return; }
    await deleteProject(id);
    setProjects((prev) => prev.filter((p) => p.id !== id));
    setDeletingId(null);
  };

  const filteredProjects = projects.filter((p) => {
    if (filterClient && p.clientId !== filterClient) return false;
    if (filterStatus && p.status !== filterStatus) return false;
    return true;
  });

  // Group filtered projects by client
  const groupedByClient = clients
    .map((client) => ({
      client,
      projects: filteredProjects.filter((p) => p.clientId === client.id),
    }))
    .filter((group) => group.projects.length > 0);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div style={{ padding: '32px', maxWidth: 960, margin: '0 auto' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.3px' }}>Projects</h1>
          <p style={{ color: '#64748B', fontSize: 14, marginTop: 4 }}>
            {projects.length} total · {projects.filter((p) => p.status === 'active').length} active
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} className="btn btn-primary">
          + New Project
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
        <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} style={{ width: 180 }}>
          <option value="">All Clients</option>
          {clients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ width: 140 }}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="completed">Completed</option>
          <option value="archived">Archived</option>
        </select>
        {(filterClient || filterStatus) && (
          <button onClick={() => { setFilterClient(''); setFilterStatus(''); }} className="btn btn-ghost btn-sm" style={{ color: '#DC2626' }}>
            × Clear
          </button>
        )}
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="card fade-in" style={{ padding: 20, marginBottom: 28, border: '2px solid #EFF6FF' }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 16, color: '#0F172A' }}>New Project</h3>
          <ProjectForm clients={clients} onSave={handleCreate} onCancel={() => setShowAdd(false)} />
        </div>
      )}

      {/* Project list grouped by client */}
      {filteredProjects.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 40px', background: 'white', borderRadius: 16, border: '2px dashed #E2E8F0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📁</div>
          <div style={{ fontSize: 16, fontWeight: 600, color: '#1E293B', marginBottom: 8 }}>No projects found</div>
          <div style={{ fontSize: 14, color: '#64748B', marginBottom: 20 }}>
            {projects.length === 0 ? 'Create your first project to get started' : 'Try adjusting your filters'}
          </div>
          {projects.length === 0 && (
            <button onClick={() => setShowAdd(true)} className="btn btn-primary">+ New Project</button>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
          {groupedByClient.map(({ client, projects: clientProjects }) => (
            <div key={client.id}>
              {/* Client section header */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  paddingBottom: 10,
                  marginBottom: 10,
                  borderBottom: `3px solid ${client.color}`,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: client.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 800,
                    fontSize: 14,
                    flexShrink: 0,
                  }}
                >
                  {client.name.slice(0, 1).toUpperCase()}
                </div>
                <div style={{ fontWeight: 800, fontSize: 18, color: '#0F172A', letterSpacing: '-0.2px' }}>
                  {client.name}
                </div>
                <div style={{ fontSize: 13, color: '#94A3B8', marginLeft: 4 }}>
                  {clientProjects.length} project{clientProjects.length !== 1 ? 's' : ''}
                </div>
              </div>

              {/* Project rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {clientProjects.map((project) => {
                  const statusCfg = STATUS_CONFIG[project.status] || STATUS_CONFIG.active;
                  return (
                    <div key={project.id} className="card" style={{ padding: '14px 18px' }}>
                      {editingId === project.id ? (
                        <>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#64748B', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                            Edit Project
                          </div>
                          <ProjectForm
                            initial={project}
                            clients={clients}
                            onSave={(data) => handleUpdate(project.id, data)}
                            onCancel={() => setEditingId(null)}
                          />
                        </>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                          {/* Project name */}
                          <div style={{ fontWeight: 600, fontSize: 14, color: '#1E293B', minWidth: 200, flexShrink: 0 }}>
                            {project.name}
                          </div>
                          {/* Divider */}
                          <div style={{ width: 1, height: 20, background: '#E2E8F0', flexShrink: 0 }} />
                          {/* Notes */}
                          <div style={{ flex: 1, fontSize: 13, color: '#64748B', lineHeight: 1.5 }}>
                            {project.notes || <span style={{ color: '#CBD5E1', fontStyle: 'italic' }}>No notes</span>}
                          </div>
                          {/* Status badge */}
                          <span
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              color: statusCfg.color,
                              background: statusCfg.bg,
                              border: `1px solid ${statusCfg.color}33`,
                              borderRadius: 20,
                              padding: '2px 10px',
                              flexShrink: 0,
                            }}
                          >
                            {statusCfg.label}
                          </span>
                          {/* Actions */}
                          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            <button onClick={() => setEditingId(project.id)} className="btn btn-secondary btn-sm">
                              Edit
                            </button>
                            <button onClick={() => handleDelete(project.id)} className="btn btn-danger btn-sm">
                              {deletingId === project.id ? 'Confirm?' : 'Delete'}
                            </button>
                            {deletingId === project.id && (
                              <button onClick={() => setDeletingId(null)} className="btn btn-ghost btn-sm">Cancel</button>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
